import { useRef, useCallback } from 'react';

export interface UseStickyIntroOptions {
  scrollContainer: HTMLElement | null;
  introSteps: any[];
  cookieKey: string;
  cookieExpireDays?: number;
  delay?: number;
}

/**
 * Prevents intro.js from scrolling to top while allowing tooltip positioning
 * Returns both the cleanup function and a way to update the target scroll position
 */
const setupScrollProtection = (scrollContainer: HTMLElement, initialScrollPosition: number) => {
  let targetScrollPosition = initialScrollPosition;
  let isUserScrolling = false;
  let scrollTimeout: NodeJS.Timeout | null = null;
  let isActive = false;
  
  // Prevent scrolling to top while allowing small adjustments for tooltip positioning
  const protectScroll = () => {
    if (!isActive || isUserScrolling) return;
    
    const currentScroll = scrollContainer.scrollTop;
    const scrollDiff = Math.abs(currentScroll - targetScrollPosition);
    
    // If scroll jumped more than 100px upward, it's likely intro.js trying to scroll to top
    if (currentScroll < targetScrollPosition - 100) {
      scrollContainer.scrollTop = targetScrollPosition;
    } else if (scrollDiff > 50) {
      // Allow moderate scrolling for tooltip positioning, update target
      targetScrollPosition = currentScroll;
    }
  };
  
  // Detect manual user scrolling
  const handleUserScroll = (event?: Event) => {
    if (event && 'isTrusted' in event && !(event as Event).isTrusted) return;
    isUserScrolling = true;
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
      isUserScrolling = false;
      // Update target position after user scrolling
      targetScrollPosition = scrollContainer.scrollTop;
    }, 150);
  };
  
  // Add listeners
  scrollContainer.addEventListener('wheel', handleUserScroll, { passive: true });
  scrollContainer.addEventListener('touchmove', handleUserScroll, { passive: true });
  scrollContainer.addEventListener('scroll', handleUserScroll, { passive: true });
  
  // Activate protection after a short delay to allow initial positioning
  const activationTimeout = setTimeout(() => {
    isActive = true;
  }, 300);
  
  // Monitor scroll changes at 60fps
  const scrollInterval = setInterval(protectScroll, 16);
  
  // Return cleanup function
  return () => {
    clearTimeout(activationTimeout);
    clearInterval(scrollInterval);
    scrollContainer?.removeEventListener('wheel', handleUserScroll);
    scrollContainer?.removeEventListener('touchmove', handleUserScroll);
    scrollContainer?.removeEventListener('scroll', handleUserScroll);
    if (scrollTimeout) clearTimeout(scrollTimeout);
  };
};

/**
 * Custom hook to manage intro.js tour with scroll lock for sticky elements
 * Prevents intro.js from scrolling the container while allowing manual user scrolling
 */
export const useStickyIntro = () => {
  const introStartedRef = useRef<boolean>(false);

  const startStickyIntro = useCallback(async ({
    scrollContainer,
    introSteps,
    cookieKey,
    cookieExpireDays = 365,
    delay = 1000
  }: UseStickyIntroOptions) => {
    // Prevent multiple calls
    if (introStartedRef.current || !scrollContainer) {
      return;
    }

    introStartedRef.current = true;

    const { startIntroTour } = require('./useIntroTour');
    
    // Start intro tour after delay
    setTimeout(async () => {
      // Save current scroll position before intro starts
      const initialScrollPosition = scrollContainer.scrollTop;
      
      const introInstance = await startIntroTour(introSteps, { 
        timeout: 3000, 
        cookieKey, 
        cookieExpireDays,
        introOptions: { 
          scrollToElement: true,
          scrollPadding: 30
        }
      });

      // Only setup scroll protection if intro actually started (no cookie exists)
      if (introInstance) {
        const cleanup = setupScrollProtection(scrollContainer, initialScrollPosition);
        let cleanedUp = false;
        const runCleanup = () => {
          if (cleanedUp) return;
          cleanedUp = true;
          cleanup();
        };
        
        // Register cleanup handlers without overriding existing intro callbacks.
        try {
          if (typeof (introInstance as any).oncomplete === 'function') {
            (introInstance as any).oncomplete(runCleanup);
          }
          if (typeof (introInstance as any).onexit === 'function') {
            (introInstance as any).onexit(runCleanup);
          }
          if (typeof (introInstance as any).on === 'function') {
            (introInstance as any).on('complete', runCleanup);
            (introInstance as any).on('exit', runCleanup);
          }
        } catch (e) {
          runCleanup();
        }
      } else {
        // Reset the ref if intro didn't start (cookie already exists)
        introStartedRef.current = false;
      }
    }, delay);
  }, []);

  const resetIntro = useCallback(() => {
    introStartedRef.current = false;
  }, []);

  return {
    startStickyIntro,
    resetIntro,
    hasStarted: introStartedRef.current
  };
};
